import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';
import type { DiscussionSort } from './model/discussionSort';

const DISCUSSION_TEXT_MAX = 2000;

interface DiscussionComposerProps {
    canWrite: boolean;
    sending: boolean;
    failed: boolean;
    refreshing: boolean;
    onSend: (text: string) => Promise<boolean>;
    onRefresh: () => void;
    sort: DiscussionSort;
    onToggleSort: () => void;
}

const DiscussionComposer = ({
    canWrite,
    sending,
    failed,
    refreshing,
    onSend,
    onRefresh,
    sort,
    onToggleSort,
}: DiscussionComposerProps) => {
    const { t } = useTranslation(['Objects']);
    const [text, setText] = useState('');
    const length = text.trim().length;
    const tooLong = length > DISCUSSION_TEXT_MAX;
    const canSend = canWrite && !sending && length > 0 && !tooLong;
    const newestFirst = sort === 'newest';
    const sortLabel = newestFirst ? t('discussion.sortNewest') : t('discussion.sortOldest');

    const submit = async () => {
        if (!canSend) return;
        const saved = await onSend(text.trim());
        if (saved) setText('');
    };

    return (
        <div className="object-discussion__composer">
            <Form.Control
                as="textarea"
                rows={3}
                value={text}
                disabled={!canWrite || sending}
                placeholder={t('discussion.placeholder')}
                onChange={(event) => setText(event.target.value)}
            />
            <div className="object-discussion__actions">
                <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={!canSend}
                    onClick={() => { void submit(); }}
                >
                    {t('discussion.send')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    disabled={refreshing}
                    onClick={onRefresh}
                >
                    {t('discussion.refresh')}
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="object-discussion__sort"
                    aria-label={sortLabel}
                    title={sortLabel}
                    onClick={onToggleSort}
                >
                    <SolarIcon name={newestFirst ? 'arrow-up' : 'arrow-down'} />
                </Button>
                <span className={tooLong ? 'text-danger ms-auto' : 'text-muted ms-auto'}>
                    {length}/{DISCUSSION_TEXT_MAX}
                </span>
            </div>
            {tooLong ? (
                <p className="text-danger mb-0">{t('discussion.tooLong')}</p>
            ) : null}
            {failed ? (
                <p className="text-danger mb-0">{t('discussion.failed')}</p>
            ) : null}
            {!canWrite ? (
                <p className="text-muted mb-0">{t('discussion.closed')}</p>
            ) : null}
        </div>
    );
};

export default DiscussionComposer;
